import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  View,
  ScrollView,
  TextInput,
  Pressable,
  ActivityIndicator,
  StyleSheet,
} from "react-native";
import { format } from "date-fns";
import BottomSheet from "@components/ds/BottomSheet";
import SheetButtons from "@components/ds/SheetButtons";
import { C, RText } from "@components/ds/theme";
import { tokens } from "@components/ds/tokens";
import { notifyError, notifySuccess } from "@components/ds/notify";
import { t } from "@constants/localization";
import OTPInput from "@screens/MyTasks/OtpVerifcation/OTPInput";
import {
  getCollectedCashSummary,
  requestCollectedCash,
  verifyCollectedCash,
} from "@services/api/reconList";
import {
  CODE,
  amountProblem,
  attemptsLeftOf,
  clockOffsetMs,
  collectedCashErrorKey,
  formatCountdown,
  formatMoney,
  normalizeSummary,
  parseAmount,
  readHandledResponse,
  secondsLeft as secondsLeftOf,
  splitDbText,
} from "./helpers";

const PATH = "ReconList/collectCash";
const OTP_LENGTH = 4;
const RUPEE = "₹";

export const STEP = { AMOUNT: "AMOUNT", OTP: "OTP", DONE: "DONE" };

const EMPTY_SUMMARY = normalizeSummary(null);

/** "10:42 · ₹ 2500.00 · Name" for an earlier verified handover. */
const txnLine = (txn) => {
  const time = txn.verified_at
    ? format(new Date(txn.verified_at), "HH:mm")
    : "";
  const by = txn.verified_by_name ? ` · ${txn.verified_by_name}` : "";
  return `${time} · ${RUPEE} ${formatMoney(txn.amount)}${by}`;
};

// Codes the sheet shows in place (the request can't go through for this DB
// today); the confirm button stays disabled while one is set.
const BLOCKING = new Set([
  CODE.RECON_CLOSED,
  CODE.DB_NOT_ACTIVE,
  CODE.TRIP_VERIFIED,
  CODE.NOT_ENABLED,
  "FORBIDDEN",
]);

/**
 * "Collect Cash before recon", the partner-web modal ported to the app.
 * Three steps: amount → OTP → done. The OTP never reaches this app: the
 * delivery boy reads it off the Recon page of the partner-db app. A live
 * pending request (from the web, or an earlier open of this sheet) resumes
 * at the OTP step.
 *
 * @param row         an open-recon row: { db_id, db_text, date }
 * @param onCollected (result) after a verified handover
 */
const CollectCashSheet = ({ visible, row, onClose, onCollected }) => {
  const [step, setStep] = useState(STEP.AMOUNT);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [amountText, setAmountText] = useState("");
  const [amountError, setAmountError] = useState("");
  const [blocked, setBlocked] = useState("");
  const [pending, setPending] = useState(null);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [result, setResult] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  // Server clock minus device clock, so the countdown matches the server's
  // 15-minute window on a phone whose clock is off.
  const offsetRef = useRef(0);

  const dbId = row?.db_id;
  const ofdDate = row?.date;
  const { name: dbName } = splitDbText(row?.db_text);

  const reset = useCallback(() => {
    setStep(STEP.AMOUNT);
    setSummary(EMPTY_SUMMARY);
    setAmountText("");
    setAmountError("");
    setBlocked("");
    setPending(null);
    setOtp("");
    setOtpError("");
    setResult(null);
    setSecondsLeft(0);
  }, []);

  const startOtpStep = useCallback((request) => {
    offsetRef.current = clockOffsetMs(request?.server_time);
    setPending(request);
    setOtp("");
    setOtpError("");
    setStep(STEP.OTP);
  }, []);

  // Countdown: ticks every second while a request is pending on the OTP step.
  useEffect(() => {
    if (step !== STEP.OTP || !pending?.expires_at) return undefined;
    const tick = () =>
      setSecondsLeft(secondsLeftOf(pending.expires_at, offsetRef.current));
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [step, pending]);

  const fail = useCallback((outcome) => {
    const { code, data } = outcome;
    if (code === CODE.EXCEEDS) {
      // The cap moved under us (another tab, a refund): show the new figures.
      setSummary((prev) => normalizeSummary({ ...prev, ...data }));
      setAmountError(t(`${PATH}/amountErrors/exceeds`));
      setStep(STEP.AMOUNT);
      return;
    }
    if (code === CODE.MISMATCH) {
      const left = attemptsLeftOf(outcome);
      setOtpError(
        left === null
          ? t(`${PATH}/wrongCodeAny`)
          : left === 1
            ? t(`${PATH}/wrongCodeOne`)
            : t(`${PATH}/wrongCode`, { left: String(left) }),
      );
      setOtp("");
      return;
    }
    if (code === CODE.EXPIRED || code === CODE.LOCKED) {
      setOtpError(t(collectedCashErrorKey(code)));
      setSecondsLeft(0);
      return;
    }
    if (BLOCKING.has(code)) {
      setBlocked(t(collectedCashErrorKey(code)));
      return;
    }
    notifyError(outcome.message || t("ReconList/errorGeneric"), "");
  }, []);

  const loadSummary = useCallback(async () => {
    if (!dbId || !ofdDate) return;
    setSummaryLoading(true);
    try {
      const outcome = readHandledResponse(
        await getCollectedCashSummary({ db_id: dbId, ofd_date: ofdDate }),
      );
      if (!outcome.ok) {
        fail(outcome);
        return;
      }
      const next = normalizeSummary(outcome.data);
      setSummary(next);
      if (next.pending_request) startOtpStep(next.pending_request);
    } catch (error) {
      fail(readHandledResponse(error));
    } finally {
      setSummaryLoading(false);
    }
  }, [dbId, ofdDate, fail, startOtpStep]);

  useEffect(() => {
    if (!visible) return;
    reset();
    loadSummary();
  }, [visible, reset, loadSummary]);

  const sendOtp = useCallback(
    async ({ resend = false } = {}) => {
      const amount =
        resend && pending ? pending.amount : parseAmount(amountText);
      if (amount === null) return;
      setActionLoading(true);
      setAmountError("");
      try {
        const outcome = readHandledResponse(
          await requestCollectedCash({
            db_id: dbId,
            ofd_date: ofdDate,
            amount,
            trip_id: resend && pending ? pending.trip_id : null,
            resend,
          }),
        );
        if (!outcome.ok) {
          fail(outcome);
          return;
        }
        notifySuccess(t(`${PATH}/${resend ? "resent" : "sent"}`));
        startOtpStep(outcome.data);
      } catch (error) {
        fail(readHandledResponse(error));
      } finally {
        setActionLoading(false);
      }
    },
    [amountText, pending, dbId, ofdDate, fail, startOtpStep],
  );

  const verify = useCallback(async () => {
    if (!pending || otp.length !== OTP_LENGTH) return;
    setActionLoading(true);
    setOtpError("");
    try {
      const outcome = readHandledResponse(
        await verifyCollectedCash({
          db_id: dbId,
          ofd_date: ofdDate,
          trip_id: pending.trip_id,
          otp,
        }),
      );
      if (!outcome.ok) {
        fail(outcome);
        return;
      }
      setResult(outcome.data);
      setStep(STEP.DONE);
      if (onCollected) onCollected(outcome.data);
    } catch (error) {
      fail(readHandledResponse(error));
    } finally {
      setActionLoading(false);
    }
  }, [pending, otp, dbId, ofdDate, fail, onCollected]);

  const onAmountChange = (text) => {
    setAmountText(String(text).replace(/[^\d.]/g, ""));
    setAmountError("");
  };

  const problem = amountProblem(amountText, summary.remaining);
  const localAmountError =
    amountError ||
    (problem && problem !== "empty"
      ? t(`${PATH}/amountErrors/${problem}`)
      : "");
  const canSend =
    !summaryLoading && !blocked && summary.remaining > 0 && problem === null;
  const canVerify =
    !blocked && otp.length === OTP_LENGTH && secondsLeft > 0 && !!pending;

  const renderKv = (label, value, strong = false) => (
    <View style={styles.kv} key={label}>
      <RText size={13} weight={500} color={C.muted}>
        {label}
      </RText>
      <RText size={14} weight={strong ? 700 : 600}>
        {value}
      </RText>
    </View>
  );

  const renderAmountStep = () => (
    <ScrollView
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.kvBox}>
        {renderKv(t(`${PATH}/ofdDate`), ofdDate || "")}
        {renderKv(
          t(`${PATH}/cap`),
          `₹ ${formatMoney(summary.cash_deliverable)}`,
        )}
        {renderKv(
          t(`${PATH}/already`),
          `₹ ${formatMoney(summary.total_collected)}`,
        )}
        {renderKv(
          t(`${PATH}/upTo`),
          `₹ ${formatMoney(summary.remaining)}`,
          true,
        )}
      </View>
      <RText size={13} weight={600} style={styles.label}>
        {t(`${PATH}/amountLabel`)}
      </RText>
      <View
        style={[
          styles.inputRow,
          (summary.remaining <= 0 || !!blocked) && styles.inputDisabled,
        ]}
      >
        <RText size={18} weight={700} color={C.muted}>
          {RUPEE}
        </RText>
        <TextInput
          testID="cash-collection-amount"
          value={amountText}
          onChangeText={onAmountChange}
          keyboardType="decimal-pad"
          maxLength={10}
          editable={summary.remaining > 0 && !blocked && !summaryLoading}
          placeholder="0.00"
          placeholderTextColor={C.disabledInk}
          style={styles.input}
          allowFontScaling={false}
        />
      </View>
      {localAmountError ? (
        <RText size={12} weight={500} color={C.redText} style={styles.err}>
          {localAmountError}
        </RText>
      ) : null}
      {summary.remaining <= 0 && !summaryLoading && !blocked ? (
        <RText size={12} weight={500} color={C.amber} style={styles.err}>
          {t(`${PATH}/nothingLeft`)}
        </RText>
      ) : null}
      <RText size={12} weight={500} color={C.muted} style={styles.hint}>
        {t(`${PATH}/amountHint`)}
      </RText>
      {summary.txns.length ? (
        <View style={styles.txns}>
          <RText size={13} weight={700} style={styles.txnsTitle}>
            {t(`${PATH}/earlier`)}
          </RText>
          {summary.txns.map((txn) => (
            <RText key={String(txn.id)} size={12} weight={500} color={C.muted}>
              {txnLine(txn)}
            </RText>
          ))}
        </View>
      ) : null}
    </ScrollView>
  );

  const renderOtpStep = () => (
    <ScrollView
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.kvBox}>
        {renderKv(
          t(`${PATH}/collecting`),
          `₹ ${formatMoney(pending?.amount)}`,
          true,
        )}
        <View style={styles.kv}>
          <RText size={13} weight={500} color={C.muted}>
            {t(`${PATH}/expiresIn`)}
          </RText>
          <RText
            testID="cash-collection-countdown"
            size={14}
            weight={700}
            color={secondsLeft <= 0 ? C.redText : C.ink}
          >
            {secondsLeft <= 0
              ? t(`${PATH}/expiredLine`)
              : formatCountdown(secondsLeft)}
          </RText>
        </View>
      </View>
      <RText size={13} weight={500} style={styles.readOut}>
        {t(`${PATH}/readOut`)}
      </RText>
      <OTPInput
        value={otp}
        valueLength={OTP_LENGTH}
        onChange={(value) => {
          setOtp(value);
          if (otpError) setOtpError("");
        }}
        otpText=""
        error={!!otpError}
      />
      {otpError ? (
        <RText
          testID="cash-collection-otp-error"
          size={12}
          weight={500}
          color={C.redText}
          style={styles.err}
        >
          {otpError}
        </RText>
      ) : null}
      <Pressable
        testID="cash-collection-resend"
        onPress={() => sendOtp({ resend: true })}
        disabled={actionLoading || !!blocked}
        style={[styles.resend, (actionLoading || !!blocked) && styles.muted]}
      >
        <RText size={13} weight={600} color={C.blue}>
          {t(`${PATH}/resend`)}
        </RText>
      </Pressable>
    </ScrollView>
  );

  const renderDoneStep = () => (
    <View>
      <View style={styles.success}>
        <RText size={14} weight={700} color={C.green}>
          {t(`${PATH}/done`, {
            amount: formatMoney(result?.amount),
            name: dbName,
          })}
        </RText>
      </View>
      <View style={styles.kvBox}>
        {renderKv(
          t(`${PATH}/totalToday`),
          `₹ ${formatMoney(result?.total_collected)}`,
        )}
        {renderKv(
          t(`${PATH}/remaining`),
          `₹ ${formatMoney(result?.remaining)}`,
        )}
      </View>
    </View>
  );

  const footer =
    step === STEP.DONE ? (
      <Pressable
        testID="cash-collection-close"
        onPress={onClose}
        style={styles.closeBtn}
      >
        <RText size={13} weight={700} color={tokens.accent.fg}>
          {t(`${PATH}/close`)}
        </RText>
      </Pressable>
    ) : (
      <SheetButtons
        onCancel={onClose}
        onConfirm={step === STEP.OTP ? verify : () => sendOtp()}
        confirmLabel={t(`${PATH}/${step === STEP.OTP ? "verify" : "send"}`)}
        cancelLabel={t(`${PATH}/cancel`)}
        loading={actionLoading}
        disabled={step === STEP.OTP ? !canVerify : !canSend}
      />
    );

  return (
    <BottomSheet
      visible={visible}
      title={t(`${PATH}/title`)}
      subtitle={row?.db_text || ""}
      onClose={onClose}
      footer={footer}
    >
      {blocked ? (
        <RText
          testID="cash-collection-blocked"
          size={13}
          weight={600}
          color={C.redText}
          style={styles.blocked}
        >
          {blocked}
        </RText>
      ) : null}
      {summaryLoading ? (
        <ActivityIndicator size="large" color={C.red} style={styles.loader} />
      ) : step === STEP.AMOUNT ? (
        renderAmountStep()
      ) : step === STEP.OTP ? (
        renderOtpStep()
      ) : (
        renderDoneStep()
      )}
    </BottomSheet>
  );
};

export default CollectCashSheet;

const styles = StyleSheet.create({
  loader: { marginVertical: 40 },
  kvBox: {
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 4,
    marginBottom: 14,
    backgroundColor: C.page,
  },
  kv: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 7,
    gap: 12,
  },
  label: { marginBottom: 6 },
  inputRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 10,
    paddingHorizontal: 12,
    backgroundColor: C.surface,
  },
  inputDisabled: { backgroundColor: C.disabledBg },
  input: {
    flex: 1,
    fontSize: 18,
    fontWeight: "700",
    color: C.ink,
    paddingVertical: 10,
  },
  err: { marginTop: 6 },
  hint: { marginTop: 8, lineHeight: 17 },
  txns: { marginTop: 14, gap: 3 },
  txnsTitle: { marginBottom: 2 },
  readOut: { marginBottom: 6, lineHeight: 19 },
  resend: {
    alignSelf: "center",
    paddingVertical: 8,
    paddingHorizontal: 14,
    marginTop: 4,
  },
  muted: { opacity: 0.5 },
  success: {
    backgroundColor: C.greenBg,
    borderRadius: 10,
    padding: 12,
    marginBottom: 14,
  },
  blocked: { marginBottom: 10 },
  closeBtn: {
    alignSelf: "flex-end",
    backgroundColor: tokens.accent.bg,
    borderRadius: tokens.radius.md,
    paddingHorizontal: tokens.spacing.large,
    paddingVertical: 10,
    marginTop: tokens.spacing.medium,
  },
});
